/**
 * SessionTimerOverlay
 *
 * Permanent overlay bottom-right on the Jitsi video showing the running
 * session timer with block label, countdown, and — for per-person blocks —
 * the current member's avatar, name, country flag and timezone.
 */

import React, { useState, useRef, useMemo, useEffect, memo } from 'react';
import { useStructuredGrowth } from './useStructuredGrowth';
import { useSessionFlow } from './useSessionFlow';
import type { SessionBlock, StructuredGrowthEntityType } from './types';

const DEFAULT_BLOCKS: SessionBlock[] = [
  { id: 'b1', type: 'intro', label: 'Introductie', durationMinutes: 5, order: 0 },
  { id: 'b2', type: 'hotseat', label: 'Leden ronde 1', durationMinutes: 9, order: 1, perPerson: true, memberSlotStart: 0, memberSlotCount: 3 },
  { id: 'b3', type: 'custom', label: 'Pauze', durationMinutes: 5, order: 2 },
  { id: 'b4', type: 'hotseat', label: 'Leden ronde 2', durationMinutes: 9, order: 3, perPerson: true, memberSlotStart: 3, memberSlotCount: 2 },
  { id: 'b5', type: 'commitment', label: 'Afsluiting', durationMinutes: 5, order: 4 },
];

const COUNTRY_FLAGS: Record<string, string> = {
  'Nederland': '🇳🇱', 'België': '🇧🇪', 'Duitsland': '🇩🇪', 'Frankrijk': '🇫🇷',
  'Verenigd Koninkrijk': '🇬🇧', 'Spanje': '🇪🇸', 'Italië': '🇮🇹', 'Oostenrijk': '🇦🇹',
  'Zwitserland': '🇨🇭', 'Denemarken': '🇩🇰', 'Zweden': '🇸🇪', 'Noorwegen': '🇳🇴',
  'Finland': '🇫🇮', 'Polen': '🇵🇱', 'Tsjechië': '🇨🇿', 'Hongarije': '🇭🇺',
  'Portugal': '🇵🇹', 'Griekenland': '🇬🇷', 'Ierland': '🇮🇪', 'Luxemburg': '🇱🇺',
  'Andere': '🌍',
};

function getFlag(country?: string): string {
  if (!country) return '';
  return COUNTRY_FLAGS[country] || '🌍';
}

function getGmtLabel(tz?: string): string {
  if (!tz) return '';
  try {
    const now = new Date();
    const offset = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value || '';
    const localTime = now.toLocaleTimeString('en-US', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: true,
    });
    return `${offset} ${localTime}`;
  } catch {
    return '';
  }
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export interface MemberMeta {
  name: string;
  avatar?: string | null;
  country?: string;
  timezone?: string;
}

interface SessionTimerOverlayProps {
  entityId: string;
  entityType?: StructuredGrowthEntityType;
  meetingId: string;
  memberCount?: number;
  memberNames?: string[];
  memberAvatars?: (string | null)[];
  /** Extra per-member metadata (country, timezone) — indexed same as memberNames */
  memberMeta?: MemberMeta[];
  /** When true (host), auto-starts the timer if it's not yet running */
  isHost?: boolean;
}

const SessionTimerOverlay: React.FC<SessionTimerOverlayProps> = memo(function SessionTimerOverlay({
  entityId, entityType = 'mastermind', meetingId,
  memberCount = 1, memberNames = [], memberAvatars = [], memberMeta = [],
  isHost = false,
}) {
  const { settings } = useStructuredGrowth(entityId, entityType);
  const blocks = (settings?.sessionStructure ?? []).length > 0 ? settings!.sessionStructure : DEFAULT_BLOCKS;

  const { currentBlock, remainingSeconds, elapsedSeconds, flow, start } = useSessionFlow(entityId, meetingId, blocks, { autoAdvance: isHost, canControl: isHost });

  // Auto-start: when host opens the meeting and timer is not yet running
  const autoStarted = useRef(false);
  useEffect(() => {
    if (isHost && !flow.isRunning && !autoStarted.current && blocks.length > 0) {
      autoStarted.current = true;
      const timer = setTimeout(() => start(), 2000);
      return () => clearTimeout(timer);
    }
  }, [isHost, flow.isRunning, blocks.length, start]);

  const [currentPersonIdx, setCurrentPersonIdx] = useState(0);
  const prevBlockRef = useRef(flow.currentBlockIndex);
  if (prevBlockRef.current !== flow.currentBlockIndex) {
    prevBlockRef.current = flow.currentBlockIndex;
    setCurrentPersonIdx(0);
  }

  const sortedBlocks = useMemo(() => [...blocks].sort((a, b) => a.order - b.order), [blocks]);

  if (!currentBlock || !flow.isRunning) return null;

  const effectiveMembers = Math.max(1, memberCount);
  const slotStart = currentBlock.memberSlotStart ?? 0;
  const slotCount = currentBlock.memberSlotCount ?? effectiveMembers;
  const isPerPerson = !!currentBlock.perPerson && slotCount > 0;

  const globalIdx = slotStart + currentPersonIdx;
  const personName = isPerPerson ? (memberNames[globalIdx] ?? `Persoon ${globalIdx + 1}`) : '';
  const personAvatar = isPerPerson ? (memberAvatars[globalIdx] ?? null) : null;
  const meta = isPerPerson ? memberMeta[globalIdx] : undefined;
  const flag = meta ? getFlag(meta.country) : '';
  const gmtLabel = meta ? getGmtLabel(meta.timezone) : '';

  const totalSec = currentBlock.durationMinutes * 60;
  const isOverTime = elapsedSeconds > totalSec;
  const displayTime = isOverTime
    ? formatTime(elapsedSeconds - totalSec)
    : formatTime(remainingSeconds);

  const isUrgent = !isOverTime && remainingSeconds <= 10;

  return (
    <div
      className="pointer-events-none select-none"
      style={{ animation: 'stoFadeIn 0.4s ease-out both' }}
    >
      <div className="flex flex-col items-center gap-1">
        {/* Avatar — only for per-person blocks */}
        {isPerPerson && (
          <div className="relative">
            {personAvatar ? (
              <img src={personAvatar} alt="" className="w-20 h-20 rounded-full object-cover border-[3px] border-white/80 shadow-2xl" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gray-700 border-[3px] border-white/80 shadow-2xl flex items-center justify-center">
                <span className="text-2xl font-bold text-white/80">{(personName || '?')[0].toUpperCase()}</span>
              </div>
            )}
            {flag && (
              <span className="absolute -bottom-1 -right-1 text-lg drop-shadow-lg">{flag}</span>
            )}
          </div>
        )}

        {/* Name or block label */}
        <p className="text-sm font-extrabold text-white uppercase tracking-wider drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)] text-center leading-tight max-w-[200px] truncate">
          {isPerPerson ? personName : currentBlock.label}
        </p>

        {/* Timer */}
        <p className={`text-[52px] font-black leading-none tabular-nums tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] ${
          isOverTime ? 'text-red-400' : isUrgent ? 'text-amber-300' : 'text-white'
        }`}>
          {isOverTime ? '+' : ''}{displayTime}
        </p>

        {/* GMT timezone label — only for per-person blocks */}
        {isPerPerson && gmtLabel && (
          <p className="text-xs font-semibold text-white/70 uppercase tracking-wide drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
            {gmtLabel}
          </p>
        )}
      </div>

      <style>{`
        @keyframes stoFadeIn {
          from { opacity: 0; transform: translateY(10px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
});

export default SessionTimerOverlay;
